/**
 * WebRTCManager — inert design-clone implementation.
 *
 * The real manager in the upstream app owned a PeerJS mesh: signalling
 * socket, RTCPeerConnection per participant, `getUserMedia`, screen capture,
 * ICE/TURN negotiation, and Web Audio speaking detection. None of that exists
 * in this clone. It cannot connect a call and is not meant to.
 *
 * What survives is the *shape*: every method the UI calls is still here with
 * the same signature, so `App.tsx`, `CallLayout`, and `VideoTile` run
 * unmodified. Joining a channel synthesises participants from
 * `fixtures/callFixtures.ts` and hands back canvas-backed MediaStreams, which
 * means the full call surface — tiles, focus/tile/cinema layouts, the control
 * ring, screen-share slots — renders and is styleable with no TURN server, no
 * webcam, and no permission prompt.
 *
 * Deliberately absent, do not "restore" them:
 *   - peerjs / RTCPeerConnection   - getUserMedia / getDisplayMedia
 *   - the /api/turn-credentials endpoint and METERED_API_KEY
 *
 * If you need real calls, take this file from the upstream repo along with
 * its `peer` server mount in `server.ts`.
 */

import {
  FIXTURE_PARTICIPANTS,
  FIXTURES_ENABLED,
  createPlaceholderStream,
} from '../fixtures/callFixtures';

interface CallEventHandlers {
  onRemoteStream: (stream: MediaStream, remotePeerId: string, isScreen?: boolean) => void;
  onLocalStream: (stream: MediaStream) => void;
  onCallEnded: (remotePeerId: string, isScreen?: boolean) => void;
  onError: (error: string) => void;
  onIncomingCall?: (offer: any, remotePeerId: string) => void;
  onDevicesListed?: (devices: { audio: MediaDeviceInfo[]; video: MediaDeviceInfo[] }) => void;
  onSpeakingChanged?: (peerId: string, isSpeaking: boolean) => void;
  onRemoteAudioStateChanged?: (peerId: string, enabled: boolean) => void;
  onLocalStreamUnavailable?: (reason: string) => void;
}

export class WebRTCManager {
  private userId: string | null = null;
  private eventHandlers: CallEventHandlers | null = null;
  private currentChannel: string | null = null;
  private localStream: MediaStream | null = null;
  private cameraTrack: MediaStreamTrack | null = null;

  /** Fixture streams handed out per peer, so we can stop them on teardown. */
  private fixtureStreams: Map<string, MediaStream> = new Map();
  /** Rotates the "who is talking" highlight while a call is open. */
  private speakingTimer: ReturnType<typeof setInterval> | null = null;

  private isDeafened = false;
  private isForceMuted = false;
  private isForcedPTT = false;
  private pttActive = false;

  constructor() {}

  public async init(userId: string) {
    this.userId = userId;
  }

  public setEventHandlers(handlers: CallEventHandlers): void {
    this.eventHandlers = handlers;
  }

  /**
   * Returns a canvas-backed stream standing in for the local camera. The
   * real implementation called `getUserMedia`; this never prompts.
   *
   * Honours the `video` constraint so toggling the camera off still yields an
   * empty stream and the UI falls back to the avatar tile.
   */
  public async getLocalStream(
    constraints: MediaStreamConstraints = { audio: true, video: true },
  ): Promise<MediaStream | null> {
    const wantsVideo = Boolean(constraints.video);

    if (!wantsVideo) {
      // Audio-only: an empty stream is enough. Nothing consumes local audio
      // because there is no peer to send it to.
      this.localStream = new MediaStream();
      this.eventHandlers?.onLocalStream(this.localStream);
      return this.localStream;
    }

    const stream = createPlaceholderStream('You', 320);
    if (!stream) {
      // Same soft-failure path the real manager used when a camera was
      // missing — the user still "joins", their tile shows the avatar.
      this.eventHandlers?.onLocalStreamUnavailable?.('NotFoundError');
      this.localStream = new MediaStream();
      this.eventHandlers?.onLocalStream(this.localStream);
      return this.localStream;
    }

    this.localStream = stream;
    this.cameraTrack = stream.getVideoTracks()[0] ?? null;
    this.eventHandlers?.onLocalStream(stream);
    return stream;
  }

  public setLocalStream(stream: MediaStream): void {
    this.localStream = stream;
    this.cameraTrack = stream.getVideoTracks()[0] ?? this.cameraTrack;
  }

  /**
   * Stands up the fixture participants for `channelId`. No signalling, no
   * negotiation — the streams are produced locally and handed straight to
   * the caller's `onRemoteStream`.
   */
  public async joinRoom(channelId: string) {
    this.currentChannel = channelId;
    if (!FIXTURES_ENABLED) return;

    for (const p of FIXTURE_PARTICIPANTS) {
      if (!p.isCameraOn) continue; // camera-off peers render as avatars
      const stream = createPlaceholderStream(p.name, p.hue);
      if (!stream) continue;
      this.fixtureStreams.set(p.id, stream);
      this.eventHandlers?.onRemoteStream(stream, p.id, false);
    }

    // One fixture peer shares their screen, so the screen-share slot and the
    // "focus the presentation" layout have something to render.
    const sharer = FIXTURE_PARTICIPANTS.find((p) => p.isScreenSharing);
    if (sharer) {
      const screen = createPlaceholderStream(`${sharer.name} · screen`, sharer.hue);
      if (screen) {
        this.fixtureStreams.set(`${sharer.id}:screen`, screen);
        this.eventHandlers?.onRemoteStream(screen, sharer.id, true);
      }
    }

    this.startSpeakingRotation();
  }

  /**
   * Cycles `onSpeakingChanged` around the fixture peers so the active-speaker
   * ring, the speaking glow, and focus-follows-speaker are all observable
   * without anyone actually talking.
   */
  private startSpeakingRotation() {
    this.stopSpeakingRotation();
    const speakers = FIXTURE_PARTICIPANTS.filter((p) => !p.isMuted);
    if (speakers.length === 0) return;

    let i = 0;
    this.speakingTimer = setInterval(() => {
      const prev = speakers[(i - 1 + speakers.length) % speakers.length];
      const next = speakers[i % speakers.length];
      if (prev && next && prev.id !== next.id) {
        this.eventHandlers?.onSpeakingChanged?.(prev.id, false);
      }
      if (next) this.eventHandlers?.onSpeakingChanged?.(next.id, true);
      i++;
    }, 2600);
  }

  private stopSpeakingRotation() {
    if (this.speakingTimer) {
      clearInterval(this.speakingTimer);
      this.speakingTimer = null;
    }
  }

  /** No mesh to reconcile — fixtures are fixed for the life of the call. */
  public syncPeers(_peerIds: string[]) {}

  public replaceVideoTrack(_newTrack: MediaStreamTrack | null, _isScreenShare = false): void {}

  public restoreCameraTrack(): void {}

  public setPermissionRestrictions(forceMuted: boolean, forcedPTT: boolean): void {
    // Kept live: the UI reads these back to grey out the mic button when a
    // role denies Speak, which is a real design state worth seeing.
    this.isForceMuted = forceMuted;
    this.isForcedPTT = forcedPTT;
  }

  public setPTTActive(active: boolean): void {
    this.pttActive = active;
  }

  public toggleAudio(enabled: boolean): void {
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = enabled));
  }

  public toggleVideo(enabled: boolean): void {
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = enabled));
  }

  public setDeafened(deafened: boolean): void {
    this.isDeafened = deafened;
  }

  public endCall(remotePeerId: string, isScreen = false): void {
    const key = isScreen ? `${remotePeerId}:screen` : remotePeerId;
    this.fixtureStreams.get(key)?.getTracks().forEach((t) => t.stop());
    this.fixtureStreams.delete(key);
    this.eventHandlers?.onCallEnded(remotePeerId, isScreen);
  }

  /**
   * The real implementation fanned a `getDisplayMedia` stream out to peers.
   * Here the caller's own screen-share UI state is what matters; we just hold
   * the stream so `stopScreenShare` can release it.
   */
  public startScreenShare(stream: MediaStream): void {
    this.fixtureStreams.set('local:screen', stream);
  }

  public stopScreenShare(): void {
    this.fixtureStreams.get('local:screen')?.getTracks().forEach((t) => t.stop());
    this.fixtureStreams.delete('local:screen');
  }

  public disconnectAll(keepLocalStream = false): void {
    this.stopSpeakingRotation();

    for (const [key, stream] of this.fixtureStreams) {
      stream.getTracks().forEach((t) => t.stop());
      const isScreen = key.endsWith(':screen');
      const peerId = isScreen ? key.slice(0, -':screen'.length) : key;
      if (peerId !== 'local') this.eventHandlers?.onCallEnded(peerId, isScreen);
    }
    this.fixtureStreams.clear();

    if (!keepLocalStream) {
      this.localStream?.getTracks().forEach((t) => t.stop());
      this.localStream = null;
      this.cameraTrack = null;
    }
    this.currentChannel = null;
  }
}

export const webRTCManager = new WebRTCManager();
